import { useState, useMemo } from 'react';

interface UseFilteringOptions<T> {
  data: T[];
  searchFields: (keyof T)[];
  filterFn?: (item: T, filters: Record<string, unknown>) => boolean;
}

export function useFiltering<T>({
  data,
  searchFields,
  filterFn,
}: UseFilteringOptions<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, unknown>>({});

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const searchMatch = searchTerm
        ? searchFields.some((field) => {
            const value = item[field];
            return value && 
              String(value).toLowerCase().includes(searchTerm.toLowerCase());
          })
        : true;

      const filterMatch = filterFn ? filterFn(item, filters) : true;

      return searchMatch && filterMatch;
    });
  }, [data, searchTerm, filters, searchFields, filterFn]);

  const updateFilter = (key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({});
  };

  return {
    searchTerm,
    setSearchTerm,
    filters,
    updateFilter,
    clearFilters,
    filteredData,
  };
}
